import { FooterPanel, RoundedButton } from "@/overlay/components";
import { intervalOptions as positiveDurationSteps } from "@/time";
import { classNames } from "@/utility/jsx";
import { TIME_NAVIGATION_STEPS } from "@/utility/key-shortcuts/shortcuts";
import { useRecentShortcut } from "@/utility/key-shortcuts/useRecentShortcut";
import { useLingui } from "@lingui/react";
import type { Duration } from "luxon";
import type { PropsWithChildren } from "react";
import style from "./style.module.scss";

type StepperProps = {
  disabled?: boolean;
  onChange?: (d: Duration) => void;
};

const negativeDurationSteps = positiveDurationSteps.map(({ duration, ...rest }) => ({
  duration: duration.negate(),
  ...rest,
}));

export const Stepper = ({ disabled, onChange, children }: PropsWithChildren<StepperProps>) => {
  useLingui();

  const shortcutStates = TIME_NAVIGATION_STEPS().map(({ minutes, shortcut }) => ({
    minutes,
    isRecent: useRecentShortcut(shortcut),
  }));

  const getIsRecent = (duration: Duration) => {
    const minutes = duration.as("minutes");
    return shortcutStates.find((state) => state.minutes === minutes)?.isRecent ?? false;
  };

  const handleStepClick = (duration: Duration) => {
    onChange?.(duration);
  };

  return (
    <FooterPanel>
      {!disabled && (
        <div className={classNames(style["time-navigation-leg"], style["time-navigation-leg--left"])}>
          {negativeDurationSteps.map(({ displayName, duration }) => {
            const name = displayName();
            return (
              <RoundedButton
                key={name}
                size="small"
                onClick={() => handleStepClick(duration)}
                active={getIsRecent(duration)}
                className={style["time-navigation-step"]}
              >
                - {name}
              </RoundedButton>
            );
          })}
        </div>
      )}
      {children}
      {!disabled && (
        <div className={style["time-navigation-leg"]}>
          {positiveDurationSteps.map(({ displayName, duration }) => {
            const name = displayName();
            return (
              <RoundedButton
                className={style["time-navigation-step"]}
                key={name}
                size="small"
                active={getIsRecent(duration)}
                onClick={() => handleStepClick(duration)}
              >
                + {name}
              </RoundedButton>
            );
          })}
        </div>
      )}
    </FooterPanel>
  );
};
